import { ALLOCATION_TYPE_METADATA } from '@/features/Collaborators/Models';
import {
    CLIENT_ALLOCATION_TYPES,
    EXCLUDED_ALLOCATION_STATUSES,
    UNKNOWN_CLIENT_LABEL,
} from '../exportsPage.definitions';
import type {
    AllocationExportItem,
    AllocationsByClientRow,
    MonthKey,
    MonthRange,
} from '../exportsPage.types';
import { buildMonthKeyList, toMonthKey } from './monthRange';

interface Bucket {
    clientName: string;
    month: MonthKey;
    scopeTypeLabel: string;
    collaboratorIds: Set<string>;
}

// Separador improvável em nome de cliente ou rótulo de tipo
const KEY_SEPARATOR = '|::|';

/**
 * Agrega alocações em linhas cliente x mês x tipo de escopo, contando pessoas
 * distintas.
 *
 * Regras:
 * - só entram os tipos com cliente (`CLIENT_ALLOCATION_TYPES`);
 * - alocações canceladas são ignoradas;
 * - a pessoa conta no mês se a alocação cobrir ao menos um dia dele;
 * - alocação sem data de término é considerada vigente até o fim do intervalo;
 * - a mesma pessoa conta uma única vez por cliente/mês/tipo, mesmo com mais de
 *   uma alocação naquela combinação.
 */
export function aggregateAllocationsByClient(
    items: AllocationExportItem[],
    range: MonthRange
): AllocationsByClientRow[] {
    const months = buildMonthKeyList(range.start, range.end);
    if (months.length === 0) {
        return [];
    }

    const buckets = new Map<string, Bucket>();

    items.forEach((item) => {
        if (!item.allocationType || !CLIENT_ALLOCATION_TYPES.includes(item.allocationType)) {
            return;
        }

        if (item.status && EXCLUDED_ALLOCATION_STATUSES.includes(item.status)) {
            return;
        }

        if (!item.startDate || Number.isNaN(item.startDate.getTime())) {
            return;
        }

        const startMonth = toMonthKey(item.startDate);
        const endMonth =
            item.endDate && !Number.isNaN(item.endDate.getTime())
                ? toMonthKey(item.endDate)
                : null;

        // Término anterior ao início indica dado inconsistente: nada a contar.
        if (endMonth && endMonth < startMonth) {
            return;
        }

        const clientName = item.clientName?.trim() || UNKNOWN_CLIENT_LABEL;
        const scopeTypeLabel = ALLOCATION_TYPE_METADATA[item.allocationType].label;

        months.forEach((month) => {
            if (month < startMonth || (endMonth && month > endMonth)) {
                return;
            }

            const key = [clientName, month, scopeTypeLabel].join(KEY_SEPARATOR);
            let bucket = buckets.get(key);
            if (!bucket) {
                bucket = { clientName, month, scopeTypeLabel, collaboratorIds: new Set() };
                buckets.set(key, bucket);
            }
            bucket.collaboratorIds.add(item.collaboratorId);
        });
    });

    return Array.from(buckets.values())
        .map(({ clientName, month, scopeTypeLabel, collaboratorIds }) => ({
            clientName,
            month,
            scopeTypeLabel,
            peopleCount: collaboratorIds.size,
        }))
        .sort(
            (a, b) =>
                a.clientName.localeCompare(b.clientName, 'pt-BR') ||
                a.month.localeCompare(b.month) ||
                a.scopeTypeLabel.localeCompare(b.scopeTypeLabel, 'pt-BR')
        );
}
